// Audit Logging Service
class AuditLogService {
  constructor() {
    this.logs = [];
    this.currentUser = this.getCurrentUser();
  }

  getCurrentUser() {
    // Get current user from localStorage or context
    const user = localStorage.getItem('currentUser');
    if (user) {
      try {
        return JSON.parse(user);
      } catch (e) {
        return { id: 'UNKNOWN', name: 'Unknown User' };
      }
    }
    return { id: 'ADMIN001', name: 'System Admin' }; // Default fallback
  }

  async logAction(action, module, details, additionalData = {}) {
    const logEntry = {
      id: Date.now() + Math.random(),
      adminId: this.currentUser.id || 'ADMIN001',
      adminName: this.currentUser.name || 'System Admin',
      action,
      module,
      details,
      timestamp: new Date().toISOString(),
      ipAddress: await this.getClientIP(),
      userAgent: navigator.userAgent,
      ...additionalData
    };

    // Store in localStorage for demo purposes
    // In production, this would be sent to a backend API
    const existingLogs = JSON.parse(localStorage.getItem('auditLogs') || '[]');
    existingLogs.unshift(logEntry);
    
    // Keep only last 1000 logs to prevent localStorage overflow
    if (existingLogs.length > 1000) {
      existingLogs.splice(1000);
    }
    
    localStorage.setItem('auditLogs', JSON.stringify(existingLogs));

    // Also send to backend API if available
    try {
      await this.sendToBackend(logEntry);
    } catch (error) {
      console.warn('Failed to send audit log to backend:', error);
    }

    return logEntry;
  }

  async getClientIP() {
    try {
      // In a real application, you might get this from your backend
      return '192.168.1.100'; // Mock IP
    } catch (error) {
      return 'Unknown';
    }
  }

  async sendToBackend(logEntry) {
    // Mock API call - replace with actual endpoint
    try {
      const response = await fetch('/api/audit-logs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(logEntry)
      });

      if (!response.ok) {
        throw new Error('Failed to send audit log');
      }

      return response.json();
    } catch (error) {
      // Silently fail for demo purposes
      throw error;
    }
  }

  async getLogs(module = null, filters = {}) {
    try {
      // Try to get from backend first
      const response = await fetch(`/api/audit-logs${module ? `?module=${module}` : ''}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.warn('Failed to fetch from backend, using localStorage:', error);
    }

    // Fallback to localStorage
    const logs = JSON.parse(localStorage.getItem('auditLogs') || '[]');
    
    if (module) {
      return logs.filter(log => log.module === module);
    }

    return logs;
  }

  // Predefined action types for consistency
  static ACTIONS = {
    CREATE: 'Created Record',
    UPDATE: 'Updated Record',
    DELETE: 'Deleted Record',
    VIEW: 'Viewed Record',
    EXPORT: 'Exported Data',
    IMPORT: 'Imported Data',
    APPROVE: 'Approved Request',
    REJECT: 'Rejected Request',
    LOGIN: 'User Login',
    LOGOUT: 'User Logout',
    ASSIGN: 'Assigned Resource',
    UNASSIGN: 'Unassigned Resource',
    ACTIVATE: 'Activated Record',
    DEACTIVATE: 'Deactivated Record',
    CONFIGURE: 'Updated Configuration',
    BACKUP: 'Created Backup',
    RESTORE: 'Restored Data'
  };

  // Helper methods for common actions
  async logCreate(module, recordId, details) {
    return this.logAction(AuditLogService.ACTIONS.CREATE, module, `Created ${module.toLowerCase()} record: ${recordId}. ${details}`);
  }

  async logUpdate(module, recordId, details) {
    return this.logAction(AuditLogService.ACTIONS.UPDATE, module, `Updated ${module.toLowerCase()} record: ${recordId}. ${details}`);
  }

  async logDelete(module, recordId, details) {
    return this.logAction(AuditLogService.ACTIONS.DELETE, module, `Deleted ${module.toLowerCase()} record: ${recordId}. ${details}`);
  }

  async logView(module, recordId, details = '') {
    return this.logAction(AuditLogService.ACTIONS.VIEW, module, `Viewed ${module.toLowerCase()} record: ${recordId}. ${details}`);
  }

  async logExport(module, details) {
    return this.logAction(AuditLogService.ACTIONS.EXPORT, module, `Exported ${module.toLowerCase()} data. ${details}`);
  }

  async logApprove(module, recordId, details) {
    return this.logAction(AuditLogService.ACTIONS.APPROVE, module, `Approved ${module.toLowerCase()} request: ${recordId}. ${details}`);
  }

  async logReject(module, recordId, details) {
    return this.logAction(AuditLogService.ACTIONS.REJECT, module, `Rejected ${module.toLowerCase()} request: ${recordId}. ${details}`);
  }

  async logAssign(module, resourceId, assigneeId, details) {
    return this.logAction(AuditLogService.ACTIONS.ASSIGN, module, `Assigned ${module.toLowerCase()} ${resourceId} to ${assigneeId}. ${details}`);
  }

  async logConfiguration(module, setting, oldValue, newValue) {
    return this.logAction(AuditLogService.ACTIONS.CONFIGURE, module, `Changed ${setting} from "${oldValue}" to "${newValue}"`);
  }
}

// Create singleton instance
const auditLogService = new AuditLogService();

export default auditLogService;
export { AuditLogService };